import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hook `useNoteDrafts(meetingId, userId)`
 *
 * Gestisce draft di una nota (body + share config) in localStorage con
 * key `meeting_notes_draft:{meetingId}:{userId}`. Debounce 800ms sul save.
 *
 * Il draft sopravvive alla chiusura del pannello; viene cancellato
 * esplicitamente via `clearDraft()` dopo un save riuscito.
 */
export function useNoteDrafts(meetingId, userId) {
    const key = meetingId && userId
        ? `meeting_notes_draft:${meetingId}:${userId}`
        : null;
    const [draft, setDraft] = useState(() => {
        if (!key) return { body: "" };
        try {
            const raw = localStorage.getItem(key);
            return raw ? JSON.parse(raw) : { body: "" };
        } catch {
            return { body: "" };
        }
    });
    const timerRef = useRef(null);

    const persist = useCallback((next) => {
        if (!key) return;
        try {
            if (!next.body || !next.body.trim()) {
                localStorage.removeItem(key);
            } else {
                localStorage.setItem(key, JSON.stringify(next));
            }
        } catch {
            /* quota or privacy mode — ignore */
        }
    }, [key]);

    // Debounce 800ms su change del draft
    useEffect(() => {
        if (!key) return;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => persist(draft), 800);
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, [draft, key, persist]);

    const clearDraft = useCallback(() => {
        if (!key) return;
        try { localStorage.removeItem(key); } catch { /* noop */ }
        setDraft({ body: "" });
    }, [key]);

    return { draft, setDraft, clearDraft };
}
